import React from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import AuthHeader from "./AuthHeader";
import AuthIllustration from "./AuthIllustration";
import CodeInput from "./CodeInput";

const TwoFactorAuth = ({
  headerProps,
  code,
  onCodeChange,
  codeLength = 6,
  onSubmit,
  submitLabel = "Continuar",
  secondaryLabel = "Tentar outra forma",
  onSecondaryAction,
  loading = false,
  error = "",
  disableSubmit = false,
  disableSecondary = false,
  allowOnlyNumbers = false
}) => {
  const handleSubmit = () => {
    if (onSubmit) onSubmit(code);
  };

  return (
    <Box
      sx={{
        alignItems: "center",
        background: "linear-gradient(180deg, #f8fafb 0%, #f4fafa 100%)",
        boxSizing: "border-box",
        display: "flex",
        justifyContent: "center",
        minHeight: "100vh",
        px: { xs: 2, sm: 3 },
        py: { xs: 4, sm: 6 },
      }}
    >
      <Box
        sx={{
          backgroundColor: "#ffffff",
          border: "1px solid #dce4ee",
          borderRadius: { xs: "14px", sm: "18px" },
          boxShadow: "0 22px 54px rgba(15, 23, 42, 0.09)",
          boxSizing: "border-box",
          display: "grid",
          gap: { xs: 2.5, sm: 3 },
          maxWidth: 520,
          overflow: "hidden",
          p: { xs: 2.5, sm: 4 },
          position: "relative",
          width: "100%",
          "&::before": {
            backgroundColor: "#5f48f6",
            borderRadius: "999px",
            content: '""',
            height: 4,
            left: { xs: 20, sm: 32 },
            position: "absolute",
            right: { xs: 20, sm: 32 },
            top: 0,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
          }}
        >
          <AuthHeader {...headerProps} />
          {headerProps?.email ? (
            <Typography
              sx={{
                color: "#334155",
                fontSize: 13,
                lineHeight: 1.45,
                mt: 0.75,
              }}
            >
              Código enviado para{" "}
              <Box component="strong" sx={{ color: "#111827", fontWeight: 700 }}>
                {headerProps.email}
              </Box>
            </Typography>
          ) : null}
        </Box>

        <AuthIllustration />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <CodeInput
            value={code}
            onChange={onCodeChange}
            length={codeLength}
            allowOnlyNumbers={allowOnlyNumbers}
          />

          {error ? (
            <Box
              sx={{
                backgroundColor: "#fff1f2",
                border: "1px solid #fecdd3",
                borderRadius: "10px",
                color: "#be123c",
                fontSize: 13,
                fontWeight: 600,
                px: 1.5,
                py: 1,
              }}
            >
              {error}
            </Box>
          ) : null}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.25,
        }}
      >
        <Button
          variant="contained"
          size="large"
          fullWidth
          sx={{
            borderRadius: 8,
            backgroundColor: "#5f48f6",
            boxShadow: "0 12px 24px rgba(95, 72, 246, 0.2)",
            fontWeight: 700,
            minHeight: 46,
            py: 1.5,
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#4f3bdc",
              boxShadow: "0 14px 28px rgba(95, 72, 246, 0.24)",
            },
          }}
          onClick={handleSubmit}
          disabled={loading || disableSubmit || code.length !== codeLength}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            submitLabel
          )}
        </Button>

        <Button
          variant="outlined"
          size="large"
          fullWidth
          sx={{
            borderColor: "#cbd6e2",
            borderRadius: 8,
            color: "#172033",
            fontWeight: 600,
            minHeight: 46,
            textTransform: "none",
            py: 1.5,
            "&:hover": {
              backgroundColor: "#f8fafc",
              borderColor: "#94a3b8",
            },
          }}
          onClick={onSecondaryAction}
          disabled={loading || disableSecondary}
        >
          {secondaryLabel}
        </Button>
      </Box>
      </Box>
    </Box>
  );
};

export default TwoFactorAuth;
